import { type CommentParentType, type CommentModel } from '../../../models/Comment'
import { CommentField } from './CommentField'
import { Box, Button } from '@mui/material'
import { BlueButton } from '../buttons/BlueButton'
import { createCommentModel, extractTaggedUserEmails } from '../../../hooks/use-comments'
import { useAuth } from '../../../hooks/use-auth'
import { useEffect, useRef, useState } from 'react'

interface NewCommentProps {
  onSubmit: (comment: CommentModel, taggedUserEmails: string[]) => void
  disabled?: boolean
  parentId: number
  parentType: CommentParentType
  onCancel?: () => void // this also determines if the button is displayed
  comment?: CommentModel
}

export const NewComment = (props: NewCommentProps): JSX.Element => {
  const { user: currentUser } = useAuth()
  const [content, setContent] = useState(props.comment?.commentText ?? '')
  const contentEditableRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // set initial content if editing a comment
    if (props.comment?.commentText != null && contentEditableRef.current != null) {
      contentEditableRef.current.innerHTML = props.comment.commentText
    }
  }, [props.comment?.commentText])

  const handleSubmit = (): void => {
    if (currentUser == null) return

    const comment: CommentModel = createCommentModel(
      props.comment?.id ?? 0,
      currentUser,
      content,
      props.comment?.isResolved ?? false,
      props.parentId,
      props.parentType
    )

    const taggedUserEmails = extractTaggedUserEmails(content)

    props.onSubmit(comment, taggedUserEmails)
    setContent('')
    if (contentEditableRef.current != null) {
      contentEditableRef.current.innerHTML = ''
    }
  }

  return <Box data-testid='newComment'>
    <CommentField
      disabled={props.disabled}
      parentId={props.parentId}
      parentType={props.parentType}
      comment={props.comment}
      contentEditableRef={contentEditableRef}
      setContent={setContent}
    />

    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      {(props.onCancel != null) &&
        <Button variant='outlined' onClick={props.onCancel}>Cancel</Button>}

      {content !== '' &&
        <BlueButton sx={{ mt: '8px' }} onClick={handleSubmit} disabled={props.disabled}>
          {props.onCancel != null ? 'Save' : 'Submit'}
        </BlueButton>}
    </Box>
  </Box>
}
